import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay } from "@icraftx/shared/v1";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { FilterPartyName } from "@/modules/core/components/DueBadge";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import { DeskDocListRowMenu } from "@/modules/shell/DeskDocListRowMenu";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import type { CustomerPaymentListRow } from "@/shared/api/sales";

const columnHelper =
  createColumnHelper<DeskTableFeatures, CustomerPaymentListRow>();

export type CustomerPaymentListCellActions = {
  listPath: string;
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
};

export function customerPaymentColumns(
  companySlug: string,
  actions: CustomerPaymentListCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const docTo = `/c/${companySlug}/sales/payments/${row.id}`;
        return (
          <DocNumberLink
            to={docTo}
            onClick={() => stashListReturn(actions.listPath, row.id)}
          >
            {info.getValue()}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("customerName", {
      id: "customerName",
      header: "Customer",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() =>
              actions.setPartyFilter(
                togglePartyFilter(actions.partyFilter, {
                  id: row.customerId,
                  name: row.customerName,
                }),
              )
            }
          />
        );
      },
    }),
    columnHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/payments/${info.row.original.id}`}
        />
      ),
    }),
    columnHelper.accessor("direction", {
      id: "direction",
      header: "Direction",
      size: 120,
      enableSorting: false,
      cell: (info) => info.getValue().replaceAll("_", " "),
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("payMethodName", {
      id: "payMethodName",
      header: "Method",
      size: 110,
      enableSorting: false,
      cell: (info) => info.getValue() ?? "—",
    }),
    columnHelper.accessor("bankRef", {
      id: "bankRef",
      header: "Bank ref",
      size: 120,
      enableSorting: false,
      cell: (info) => info.getValue() ?? "—",
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: "Updated",
      size: 88,
      enableSorting: false,
      cell: (info) => {
        const raw = info.getValue();
        return raw ? (
          <span className="text-xs text-[var(--color-muted)] tabular-nums">
            {relativeTimeLabel(raw)}
          </span>
        ) : (
          "—"
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 48,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const docPath = `/c/${companySlug}/sales/payments/${row.original.id}`;
        return <DeskDocListRowMenu docPath={docPath} tabLinks={[]} />;
      },
    }),
  ]);
}
